import axios from "axios";
import { toast } from "react-toastify";
import axiosInstance from "Utils/axiosInstance";

export type FileDirectoryItem = {
  fileid: string;
  filename: string;
  filedirectoryid?: string;
  url?: string;
  bucketid?: string;
  mimetype?: string;
};

type UploadUrlRequestItem = {
  nodeid: string;
  filedirectoryid: string;
  filename: string;
};

type UploadUrlResponseItem = {
  fileid: string;
  bucketid: string;
  url: string;
};

type DeleteFileRequestItem = {
  nodeid: string;
  fileid: string;
};

const getFilenameFromFileId = (fileid = "") => {
  const parts = fileid.split("/");
  return decodeURIComponent(parts[parts.length - 1] || fileid);
};

export const normalizeFileDirectoryItems = (data: any): FileDirectoryItem[] => {
  const items = Array.isArray(data)
    ? data
    : data?.files ?? data?.filedirectory?.files ?? [];
  const responseFileDirectoryId =
    data?.filedirectoryid ?? data?.filedirectory?.filedirectoryid;

  return items.map((item: any) => {
    const file = item.file ?? item;
    // The filedirectory listing returns the object path as `name`; keep that as
    // the id, but display only the final filename so long directories fit the UI.
    const fileid = file.fileid ?? file.id ?? file.name ?? file.filename ?? "";

    return {
      fileid,
      filename: file.filename ?? getFilenameFromFileId(file.name ?? fileid),
      filedirectoryid: file.filedirectoryid ?? responseFileDirectoryId,
      url: file.url,
      bucketid: file.bucketid,
      mimetype: file.mimetype,
    };
  });
};

export const getFileDirectoryFilesAPI = async (
  objectNodeId: string,
  filedirectoryid: string
) => {
  const response = await axiosInstance.get(
    `/filedirectories/${encodeURIComponent(objectNodeId)}/${encodeURIComponent(
      filedirectoryid
    )}`
  );

  return normalizeFileDirectoryItems(response.data);
};

export const requestFileDirectoryUploadUrlsAPI = async (
  filenames: UploadUrlRequestItem[]
): Promise<UploadUrlResponseItem[]> => {
  const response = await axiosInstance.post("/filedirectories", {
    filenames,
  });

  return response.data;
};

export const deleteFilesAPI = async (files: DeleteFileRequestItem[]) => {
  return axiosInstance.delete("/files", {
    data: { files },
  });
};

export const uploadFilesToFileDirectory = async (
  uploadUrls: UploadUrlResponseItem[],
  files: File[]
) => {
  // File directories upload raw file contents directly to the presigned MinIO
  // URLs returned by KH; no attachment object is created for measurement files.
  await Promise.all(
    uploadUrls.map((uploadMeta, index) => {
      const file = files[index];
      if (!file) return Promise.resolve();

      const toastId = toast(`Uploading ${file.name}...`, {
        progress: 0,
        autoClose: false,
      });

      return axios
        .put(uploadMeta.url, file, {
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );

            toast.update(toastId, {
              render: `${file.name} (${percent}%)`,
              progress: percent / 100,
            });
          },
        })
        .then(() => {
          toast.update(toastId, {
            render: `${file.name} uploaded successfully`,
            type: "success",
            progress: 1,
            autoClose: 3000,
          });
        });
    })
  );
};
